import { SHEET_ZOOM_FIT, Size, nextSheetZoom, t } from '@bms2/shared';
import { Button, IconButton } from '../controls';
import styles from './SheetZoomControl.module.css';

/**
 * 🔒 The zoom a framed sheet is read at (decisions 0577, 0611, 0659): zoom out,
 * zoom in, and a reset to fit page, over the six quarter steps from half size to
 * double that `nextSheetZoom` walks.
 *
 * 🔒 One control, drawn in two places: the print template editor's preview and
 * the PDF view of every accounting document. The product owner, T188: *"zoom in
 * and out control that is present in the template editor needs to be present in
 * the pdf view of accounting documents"* — the same control, not a second one.
 *
 * 🔨 It holds no state. The step is the caller's, because the editor remembers
 * it per device and the document view opens at fit page every time, which is
 * what keeps the document view instant (decision 0627).
 *
 * 🔨 An end of the scale is a dead button that says why, never a button that
 * silently does nothing (decision 0559).
 *
 * 🔒 Compact, on a phone's PDF view, the steps are − and + icon buttons named Zoom
 * Out and Zoom In to a reader, so the switch, the zoom, the count and the download
 * share one row (T2190; decision 3051). Fit Page keeps its words.
 */
export function SheetZoomControl({ zoom, onZoom, attributes, fitPage = true, onFitPage, compact = false }: {
  /** The step in force, in percent of the fit in force. */
  zoom: number;
  /** Called with the step a press moves to; never called with the step already in force. */
  onZoom: (next: number) => void;
  /**
   * Whether the fit in force is the whole page. A sheet opened fitted to the
   * pane's width is not (decision 1452), so Fit page stays live at its first step.
   */
  fitPage?: boolean;
  /** The press on Fit page, where the caller holds a fit of its own; without one it steps to 100. */
  onFitPage?: () => void;
  /** The handle the caller's own suites read the step by, such as `data-print-zoom`. */
  attributes?: Readonly<Record<`data-${string}`, string | number>>;
  /** The phone's one-row shape: the two steps as − and + (decision 3051). */
  compact?: boolean;
}) {
  const out = nextSheetZoom(zoom, -1);
  const inward = nextSheetZoom(zoom, 1);
  return (
    <div className={styles.bar} data-sheet-zoom={zoom} {...(compact ? { 'data-sheet-zoom-shape': 'compact' } : {})} {...attributes}>
      {compact ? (
        <>
          <IconButton meaning="zoomOut" label={t('sheet.zoom.out')} size={Size.Small} name="sheet-zoom-out" disabled={out === null} disabledReason={t('sheet.zoom.smallest')} onClick={() => (out === null ? undefined : onZoom(out))} />
          <IconButton meaning="zoomIn" label={t('sheet.zoom.in')} size={Size.Small} name="sheet-zoom-in" disabled={inward === null} disabledReason={t('sheet.zoom.largest')} onClick={() => (inward === null ? undefined : onZoom(inward))} />
        </>
      ) : (
        <>
          <Button size={Size.Small} text={[t('sheet.zoom.out')]} disabled={out === null} disabledReason={t('sheet.zoom.smallest')} actions={[() => (out === null ? undefined : onZoom(out))]} />
          <Button size={Size.Small} text={[t('sheet.zoom.in')]} disabled={inward === null} disabledReason={t('sheet.zoom.largest')} actions={[() => (inward === null ? undefined : onZoom(inward))]} />
        </>
      )}
      <Button size={Size.Small} text={[t('sheet.zoom.fit')]} disabled={fitPage && zoom === SHEET_ZOOM_FIT} disabledReason={t('sheet.zoom.alreadyFit')} actions={[() => (onFitPage ? onFitPage() : onZoom(SHEET_ZOOM_FIT))]} />
    </div>
  );
}
